import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { getMenuItemById, genericErrorToast, infiniteScrollItemsFetchLimit, hasOrgPermission, isDefaultOrgUser } from '@app/helpers';
import {
    IListPageBarEnabled,
    OrderType,
    Order,
    ITableHead,
    IVaultsDisplay,
    IDotMenu,
    FilterEnum,
    IActiveFilter,
} from '@app/models';
import { IModalData, ModalComponent, PasslyButton, PasslyTable } from '@app/components';
import { setExceptionHandler, resetScroll, getModalSwitch } from '@app/store/actions';
import { Box, TableCell } from '@material-ui/core';
import { vaultsService, basicService } from '@app/services';
import { Add } from '@material-ui/icons';
import { ManageVault, ManageSystem } from '@app/store/constants';
import VaultExtraDetailsComponent from './components/vaultExtraDetails.component';
import { confirm } from '@app/components/Modal/confirmModal';
import { AddVaultDialog } from '@app/pages/password/dialogs/addVault.dialog';
import { PasslyTooltip } from '@app/componentsV2';

interface ITableData {
    checked: boolean;
    id: string;
    cutId: string;
    displayName: string;
    displayNameTooltip: string;
    orgName: string;
    vaultType: string;
    lastModified: Date;
    canUpdate: boolean;
    canDelete: boolean;
    cantDelete: boolean;
    passwordCount: number;
    folderCount: number;
    isIndividual: boolean;
    checkboxDisabled: boolean;
    onClick: (event: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>) => void;
}

enum TableHeadEnum {
    id = 'id',
    cutId = 'cutId',
    displayName = 'displayName',
    lastModified = 'lastModified',
    vaultType = 'vaultType',
    orgOnBehalfOfName = 'orgName',
    onBehalfOfOrgId = 'onBehalfOfOrgId',
}

const VaultManagerPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.VaultManager);

    const individualTypeText = 'Individual';
    const vaultTypes: any[] = [
        { value: individualTypeText, label: 'INDIVIDUAL' },
        { value: 'Shared', label: 'SHARED' }
    ];

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [loadingData, setLoadingData] = useState(true);
    const [rows, setRows] = useState<ITableData[]>([]);
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [orgAllowedToViewThisPage, setOrgAllowedToViewThisPage] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState(0);
    const [vaultTypeSearch, setVaultTypeSearch] = useState('');
    const [filters, setFilters] = useState<IActiveFilter[]>([]);
    const [onBehalfOfSearch, setOnBehalfOfSearch] = useState('');
    const [resetScrollCount, setResetScrollCount] = useState(0);

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: false
    };

    const getComplexCellCutId = (row: ITableData) => {
        return (
            <PasslyTooltip title={row.id}>
                <TableCell onClick={row.onClick}>{row.cutId}</TableCell>
            </PasslyTooltip>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.id,
            label: 'VAULT_ID',
            hideSorting: true,
            width: '150px',
            type: 'getComplexCell',
            getComplexCell: getComplexCellCutId,
            filterType: FilterEnum.cast,
        },
        {
            id: TableHeadEnum.displayName,
            label: 'VAULT_NAME',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.displayName,
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'VAULTS_LAST_MODIFIED',
            type: 'date'
        },
        {
            id: TableHeadEnum.vaultType,
            label: 'TYPE',
            filterType: FilterEnum.contains,
            suggestionsForFilter: vaultTypes
        },
        {
            id: TableHeadEnum.orgOnBehalfOfName,
            label: 'ORGANIZATION',
            hidden: !isDefaultOrgUser(),

        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(setExceptionHandler(true));
    }, []);

    useEffect(() => {
        setOrgAllowedToViewThisPage(true);
    }, [organizationFilter]);

    useEffect(() => {
        if (!loadingData) {
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [filters]);

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        setLoadingData(true);


        vaultsService.getModifiableVaults(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            organizationFilter,
            handleError,
            handleSuccess,
            isReset,
            true,
            '',
            vaultTypeSearch,
            onBehalfOfSearch,
            filters
        );
    };

    const handleSuccess = (response: any) => {
        // if it didn't throw an error then it is true
        setOrgAllowedToViewThisPage(true);

        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setTotalCount(count);
        }

        const results: IVaultsDisplay[] = response.data.value;

        if (results) {
            let newRows = results.map((row: IVaultsDisplay) => {
                const isIndividual: boolean = row.VaultType === individualTypeText;
                const vaultTypeForDisplay: string = isIndividual ? translation('INDIVIDUAL') : translation('SHARED');

                // display name can get really long and that looks bad on the screen. Let's to the ... thing
                let fixDisplayName: string = row.DisplayName;

                if (fixDisplayName.length > 50) {
                    fixDisplayName = fixDisplayName.substr(0, 50).trim() + '...';
                }

                const data: ITableData = {
                    checked: false,
                    checkboxDisabled: isIndividual,
                    id: row.Id,
                    cutId: `...${row.Id.slice(-12)}`,
                    displayName: fixDisplayName,
                    displayNameTooltip: row.DisplayName,
                    vaultType: vaultTypeForDisplay,
                    lastModified: row.LastModified,
                    orgName: row.OrgName,
                    canUpdate: hasOrgPermission([ManageVault, ManageSystem]),
                    cantDelete: isIndividual ? true : !hasOrgPermission([ManageVault, ManageSystem]),
                    canDelete: isIndividual ? false : hasOrgPermission([ManageVault, ManageSystem]),
                    passwordCount: row.PasswordCount,
                    folderCount: row.FolderCount,
                    isIndividual: isIndividual,
                    onClick: editVaultClick(row.Id, vaultTypeForDisplay)
                };

                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const handleError = (error: any) => {
        setLoadingData(false);
        setRows([]);

        if (error.response.status === 400 && error.response.data.error.message === 'ORG_CANNOT_VIEW') {
            setOrgAllowedToViewThisPage(false);
        } else {
            genericErrorToast(error);
        }
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;

        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(resetScroll());
    };

    const openVaultDetails = (row: ITableData) => () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: row.displayName,
            modalSwitcher: modalSwitchState,
            form: (
                <VaultExtraDetailsComponent
                    vaultName={row.displayName}
                    vaultType={row.vaultType}
                    passwordCount={row.passwordCount}
                    folderCount={row.folderCount}
                    vaultId={row.id}
                    isIndividual={row.isIndividual}
                />
            ),
            headerClasses: 'modal__header--lg',
            bodyClasses: 'modal__body--borderless'
        });
    };

    const addVault = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_VAULT'),
            modalSwitcher: modalSwitchState,
            form: <AddVaultDialog handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            setResetScrollCount(resetScrollCount + 1);
            setTotalCount(0);
        }
    };

    const goToVault = (row: any) => () => {
        navigateToVaultDetails(row.id, row.vaultType, true);
    };

    const editVaultClick = (id: string, vaultType: string) => () => {
        navigateToVaultDetails(id, vaultType, false);
    };

    const navigateToVaultDetails = (id: string, individual: string, openInNewTab: boolean) => {
        const ind: string = individual === individualTypeText ? 'true' : 'false';

        basicService.redirect(`/passwords/details/${id}?view=1&individual=${ind}`, openInNewTab);
    };

    const deleteSelected = () => {
        let ids: string[] = [];
        rows.forEach((row) => {
            if (row.checked && row.canDelete) {
                ids.push(row.id);
            }
        });

        deleteVaultDialog(ids);
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deleteVaultDialog(ids);
    };

    const deleteVaultDialog = (ids: string[]) => {
        confirm(translation('DELETE_VAULT_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_VAULT_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteVaults(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteVaults = (ids: string[]) => {
        let json: any = {};

        json.Vaults = [];

        ids.forEach(function (id) {
            json.Vaults.push(id);
        });

        vaultsService.deleteVaults(json, genericErrorToast, handleDeleteSuccess);
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    return (
        <>
            <ModalComponent {...modalData} />

            <div className='passwordVaults'>
                <div className='vaults__section-head-inputs vaults__section-head-inputs-right'>
                    {hasOrgPermission([ManageVault, ManageSystem]) && orgAllowedToViewThisPage && (
                        <Box m={1}>
                            <PasslyButton color='primary' variant='contained' onClick={addVault}>
                                <Add color='inherit' />
                                {translation('NEW')}
                            </PasslyButton>
                        </Box>
                    )}
                </div>
            </div>

            {orgAllowedToViewThisPage ? (
                <PasslyTable
                    rows={rows}
                    setRows={setRows}
                    showRowsNumbers={true}
                    order={order}
                    orderBy={orderBy}
                    handleOrderChange={createSortHandler}
                    headerItems={headCells}
                    rowsCount={totalCount}
                    onFetch={fetchMoreData}
                    withCheckboxes={hasOrgPermission([ManageVault, ManageSystem])}
                    emptyListHelper={translation('VAULTS_EMPTY_LIST')}
                    loadingData={loadingData}
                    setFilters={setFilters}
                    openDetails={openVaultDetails}
                    openInNewTab={goToVault}
                    deleteRow={deleteRow}
                    resetLocal={resetScrollCount}
                    actionMenuItems={hasOrgPermission([ManageVault, ManageSystem]) ? actionMenuItems : undefined}
                />
            ) : (
                <div className='table--empty'>{translation('ORG_CANNOT_VIEW')}</div>
            )}
        </>
    );
};

export default VaultManagerPage;
